import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Mail, Phone, Calendar, Tag, Trash2, MailOpen, RefreshCw,
    ChevronDown, ChevronUp, ExternalLink, Inbox
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../services/api";

const EVENT_COLOR = {
    "Koncert":          "text-nova-cyan   border-nova-cyan/30   bg-nova-cyan/8",
    "Festival":         "text-amber-400   border-amber-400/30   bg-amber-400/8",
    "Firemná akcia":    "text-purple-400  border-purple-400/30  bg-purple-400/8",
    "Konferencia":      "text-blue-400    border-blue-400/30    bg-blue-400/8",
    "Svadba":           "text-pink-400    border-pink-400/30    bg-pink-400/8",
    "Kultúrne podujatie":"text-emerald-400 border-emerald-400/30 bg-emerald-400/8",
    "Súkromná oslava":  "text-orange-400  border-orange-400/30  bg-orange-400/8",
};

function formatDate(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("sk-SK", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
}

function ContactCard({ contact, onRead, onDelete }) {
    const [open, setOpen] = useState(!contact.read);
    const [deleting, setDeleting] = useState(false);
    const evCls = EVENT_COLOR[contact.event_type] || "text-zinc-400 border-zinc-700 bg-zinc-800/40";

    const handleRead = async () => {
        if (contact.read) return;
        try {
            await onRead(contact.id);
        } catch {
            toast.error("Nepodarilo sa označiť ako prečítané");
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(`Zmazať dopyt od ${contact.name}?`)) return;
        setDeleting(true);
        try {
            await onDelete(contact.id);
            toast.success("Dopyt zmazaný");
        } catch {
            toast.error("Chyba pri mazaní");
            setDeleting(false);
        }
    };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
            className={`rounded-2xl border transition-colors overflow-hidden ${
                contact.read ? "border-white/6 bg-nova-surface/25" : "border-nova-cyan/20 bg-nova-cyan/5"
            }`}
        >
            {/* Header row */}
            <div
                className="flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-white/3 transition-colors"
                onClick={() => { setOpen(v => !v); if (!contact.read) handleRead(); }}
            >
                {/* Unread dot */}
                <div className={`w-2 h-2 rounded-full shrink-0 ${contact.read ? "bg-transparent" : "bg-nova-cyan"}`} />

                {/* Avatar */}
                <div className="w-9 h-9 rounded-full bg-nova-surface/80 border border-white/10 flex items-center justify-center shrink-0 font-heading font-black text-sm text-white">
                    {contact.name.charAt(0).toUpperCase()}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-heading font-bold text-sm ${contact.read ? "text-zinc-300" : "text-white"}`}>
                            {contact.name}
                        </span>
                        {contact.event_type && (
                            <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full border ${evCls}`}>
                                {contact.event_type}
                            </span>
                        )}
                        {!contact.read && (
                            <span className="font-mono text-[9px] px-2 py-0.5 rounded-full bg-nova-cyan/15 text-nova-cyan border border-nova-cyan/25 uppercase tracking-wider">
                                Nové
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-zinc-500 text-xs font-mono">
                        <span className="truncate">{contact.email}</span>
                        {contact.event_date && (
                            <span className="flex items-center gap-1 shrink-0">
                                <Calendar className="w-3 h-3" />{contact.event_date}
                            </span>
                        )}
                    </div>
                </div>

                {/* Date + toggle */}
                <div className="text-zinc-600 text-[11px] font-mono text-right shrink-0 hidden sm:block">
                    {formatDate(contact.created_at)}
                </div>
                <div className="text-zinc-600 shrink-0">
                    {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
            </div>

            {/* Expanded body */}
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: "auto" }}
                        exit={{ height: 0 }}
                        className="overflow-hidden"
                    >
                        <div className="px-5 pb-5 pt-1 border-t border-white/6">
                            {/* Meta grid */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                                {contact.phone && (
                                    <div className="flex items-center gap-2 text-sm">
                                        <Phone className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                                        <a href={`tel:${contact.phone}`} className="text-zinc-300 hover:text-white transition-colors font-mono text-xs">
                                            {contact.phone}
                                        </a>
                                    </div>
                                )}
                                <div className="flex items-center gap-2 text-sm">
                                    <Mail className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                                    <a href={`mailto:${contact.email}`} className="text-zinc-300 hover:text-white transition-colors font-mono text-xs truncate">
                                        {contact.email}
                                    </a>
                                </div>
                                {contact.event_date && (
                                    <div className="flex items-center gap-2 text-sm">
                                        <Calendar className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                                        <span className="text-zinc-300 font-mono text-xs">{contact.event_date}</span>
                                    </div>
                                )}
                                {contact.event_type && (
                                    <div className="flex items-center gap-2 text-sm">
                                        <Tag className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                                        <span className="text-zinc-300 font-mono text-xs">{contact.event_type}</span>
                                    </div>
                                )}
                            </div>

                            {/* Message */}
                            <div className="bg-nova-black/40 border border-white/6 rounded-xl p-4 mb-4">
                                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-600 mb-2">Správa</div>
                                <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap">{contact.message}</p>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-2">
                                <a
                                    href={`mailto:${contact.email}?subject=Re: ${contact.event_type || "Váš dopyt"} — KB MUSIC`}
                                    className="inline-flex items-center gap-1.5 h-8 px-4 rounded-lg bg-nova-cyan text-black text-xs font-semibold hover:saturate-125 transition-all"
                                >
                                    <ExternalLink className="w-3 h-3" /> Odpovedať
                                </a>
                                {!contact.read && (
                                    <button
                                        onClick={handleRead}
                                        className="inline-flex items-center gap-1.5 h-8 px-4 rounded-lg border border-white/10 text-zinc-400 text-xs hover:border-white/25 hover:text-white transition-all"
                                    >
                                        <MailOpen className="w-3 h-3" /> Označiť ako prečítané
                                    </button>
                                )}
                                <button
                                    onClick={handleDelete}
                                    disabled={deleting}
                                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-red-500/15 text-red-500/60 text-xs hover:border-red-500/40 hover:text-red-400 transition-all ml-auto disabled:opacity-40"
                                >
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}

export default function AdminContacts() {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading]   = useState(true);
    const [filter, setFilter]     = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const data = await api.getContacts();
            setContacts(data || []);
        } catch {
            toast.error("Nepodarilo sa načítať dopyty");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleRead = async (id) => {
        await api.markContactRead(id);
        setContacts(cs => cs.map(c => c.id === id ? { ...c, read: true } : c));
    };

    const handleDelete = async (id) => {
        await api.deleteContact(id);
        setContacts(cs => cs.filter(c => c.id !== id));
    };

    const unread = contacts.filter(c => !c.read).length;
    const filtered = filter === "unread" ? contacts.filter(c => !c.read) : contacts;

    return (
        <div className="max-w-3xl space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-2xl font-black text-white tracking-tight">Dopyty</h1>
                    <p className="text-zinc-500 text-sm mt-1">
                        Správy z kontaktného formulára.
                        {unread > 0 && (
                            <span className="ml-2 font-mono text-nova-cyan">{unread} neprečítaných</span>
                        )}
                    </p>
                </div>
                <button
                    onClick={load} disabled={loading}
                    className="flex items-center gap-1.5 text-zinc-500 hover:text-white transition-colors text-xs font-mono"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                    Obnoviť
                </button>
            </div>

            {/* Filter tabs */}
            {contacts.length > 0 && (
                <div className="flex gap-2">
                    {[
                        { val: "all",    label: `Všetky (${contacts.length})` },
                        { val: "unread", label: `Neprečítané (${unread})`     },
                    ].map(t => (
                        <button
                            key={t.val}
                            onClick={() => setFilter(t.val)}
                            className={`h-8 px-4 rounded-lg text-xs font-mono border transition-all ${
                                filter === t.val
                                    ? "border-nova-cyan/40 bg-nova-cyan/8 text-nova-cyan"
                                    : "border-white/8 text-zinc-500 hover:border-white/20 hover:text-zinc-300"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            )}

            {/* List */}
            {loading ? (
                <div className="space-y-3">
                    {[0,1,2].map(i => (
                        <div key={i} className="h-20 rounded-2xl bg-nova-surface/30 animate-pulse" />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 gap-3">
                    <Inbox className="w-10 h-10 text-zinc-700" />
                    <p className="text-zinc-600 text-sm font-mono uppercase tracking-wider">
                        {filter === "unread" ? "Žiadne neprečítané správy" : "Zatiaľ žiadne dopyty"}
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    <AnimatePresence mode="popLayout">
                        {filtered.map(c => (
                            <ContactCard
                                key={c.id}
                                contact={c}
                                onRead={handleRead}
                                onDelete={handleDelete}
                            />
                        ))}
                    </AnimatePresence>
                </div>
            )}
        </div>
    );
}
